import React from "react";
import { Link } from "react-router-dom";
import { Target, Eye, Heart, Award, BookOpen, Globe, Music, Mic, ArrowRight, Clapperboard, Briefcase, Laptop } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import Reveal from "@/components/Reveal";
import GoldCross from "@/components/GoldCross";
import Seo from "@/components/Seo";
import { MINISTRY } from "@/data/site";

const values = [
  { icon: Heart, label: "Foi" },
  { icon: Award, label: "Excellence" },
  { icon: BookOpen, label: "Vérité biblique" },
  { icon: Mic, label: "Service" },
  { icon: Globe, label: "Impact des nations" },
];

const About = () => {
  return (
    <div>
      <Seo
        title="À propos — Prophète Steeve Orphôss"
        description="Découvrez le parcours du Prophète Steeve Orphôss (Stezo l'Impériale) : prophète, auteur chrétien, chef de chantre depuis 2003, designer IA et entrepreneur digital. Mission, vision et valeurs du ministère international."
        path="/a-propos"
      />
      <PageHeader
        overline="Le serviteur & sa vocation"
        title="À propos du Prophète Steeve Orphôss"
        subtitle="Une vie consacrée à la proclamation de l'Évangile, à la louange et à l'édification du Corps du Christ."
      />

      {/* Intro */}
      <section className="py-20 md:py-28 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
          <Reveal>
            <div className="relative">
              <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-tr from-gold/25 to-transparent blur-2xl" />
              <div className="relative rounded-[1.8rem] overflow-hidden border border-gold/30 shadow-xl bg-gradient-to-b from-[#FBF3DE] to-[#F3E4BC]">
                <img
                  src={MINISTRY.portrait}
                  alt="Prophète Steeve Orphôss"
                  className="w-full h-auto object-contain"
                />
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.15}>
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-4">Présentation du ministère</p>
            <h2 className="font-heading text-3xl md:text-4xl font-light text-brown-black tracking-tight mb-6">
              Un appel prophétique pour cette génération
            </h2>
            <div className="space-y-5 text-brown-black/75 leading-relaxed">
              <p>
                Depuis 2003, le Prophète Steeve Orphôss exerce comme chef de chantre, auteur-compositeur, entrepreneur
                et serviteur de Dieu. Son parcours témoigne d'une fidélité constante à l'appel reçu : porter la voix de
                Dieu auprès des nations à travers la louange, l'enseignement et la prophétie.
              </p>
              <p>
                Auteur, compositeur, interprète, producteur audiovisuel et entrepreneur, il met ses talents au service
                de l'Évangile. Il développe également des projets dans les domaines des médias, de la formation, de
                l'innovation digitale, du développement web, de l'intelligence artificielle et des industries créatives,
                avec pour objectif d'impacter les générations et de promouvoir des valeurs d'excellence.
              </p>
              <p>
                À travers ses entreprises et ses initiatives, il accompagne également les organisations, les ministères
                et les entrepreneurs dans leur transformation numérique, la création de contenus, le développement de
                plateformes digitales et la communication stratégique.
              </p>
              <p>
                Son œuvre musicale, audiovisuelle et entrepreneuriale est un puissant instrument d'édification,
                inspirant et transformant des vies bien au-delà des frontières.
              </p>
              <p>
                Sa mission demeure : <span className="text-brown-dark font-semibold">annoncer l'Évangile, enseigner,
                édifier et impacter les nations</span> par la Parole de Dieu et l'action du Saint-Esprit.
              </p>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-8">
              <div className="flex items-center gap-3">
                <Music className="text-gold shrink-0" size={22} />
                <span className="text-sm font-semibold text-brown-dark">Œuvre musicale</span>
              </div>
              <div className="flex items-center gap-3">
                <Clapperboard className="text-gold shrink-0" size={22} />
                <span className="text-sm font-semibold text-brown-dark">Production audiovisuelle</span>
              </div>
              <div className="flex items-center gap-3">
                <Briefcase className="text-gold shrink-0" size={22} />
                <span className="text-sm font-semibold text-brown-dark">Entrepreneuriat</span>
              </div>
              <div className="flex items-center gap-3">
                <Laptop className="text-gold shrink-0" size={22} />
                <span className="text-sm font-semibold text-brown-dark">Innovation digitale</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-[#FBF6E9] to-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8">
          <Reveal>
            <div className="h-full bg-white rounded-2xl p-10 border border-gold/20" style={{ boxShadow: "0 8px 30px rgba(42,22,8,0.05)" }} data-testid="mission-card">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-gold/10 text-gold mb-6">
                <Target size={26} />
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-medium text-brown-black mb-4">Mission</h3>
              <p className="text-brown-black/75 leading-relaxed text-lg">
                Porter un message de foi, de repentance, de restauration et d'édification pour cette génération.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="h-full bg-white rounded-2xl p-10 border border-gold/20" style={{ boxShadow: "0 8px 30px rgba(42,22,8,0.05)" }} data-testid="vision-card">
              <div className="flex items-center justify-center w-14 h-14 rounded-full bg-gold/10 text-gold mb-6">
                <Eye size={26} />
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-medium text-brown-black mb-4">Vision</h3>
              <p className="text-brown-black/75 leading-relaxed text-lg">
                Voir des vies transformées par la Parole de Dieu, la louange, l'enseignement et l'action du Saint-Esprit.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 md:py-24 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.3em] text-gold font-semibold mb-4">Nos fondements</p>
            <h2 className="font-heading text-4xl md:text-5xl font-light text-brown-black tracking-tight">Valeurs</h2>
            <div className="gold-line w-24 mx-auto my-8" />
          </Reveal>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-6 mt-4">
            {values.map((v, i) => (
              <Reveal key={v.label} delay={i * 0.08}>
                <div className="bg-white rounded-2xl p-8 border border-transparent hover:border-gold transition-all duration-300 hover:-translate-y-1" style={{ boxShadow: "0 8px 30px rgba(42,22,8,0.04)" }}>
                  <div className="flex items-center justify-center w-14 h-14 mx-auto rounded-full bg-gold/10 text-gold mb-5">
                    <v.icon size={24} />
                  </div>
                  <p className="font-heading text-xl font-medium text-brown-black">{v.label}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-3xl bg-brown-dark px-8 py-14 md:px-16 text-center md:text-left flex flex-col md:flex-row items-center justify-between gap-6">
            <GoldCross className="absolute right-10 top-1/2 -translate-y-1/2 h-[140%] w-60" opacity={0.1} />
            <div className="relative">
              <h3 className="font-heading text-3xl md:text-4xl font-light text-ivory">Rejoignez l'œuvre prophétique</h3>
              <p className="text-ivory/70 mt-3">Découvrez les enseignements et soutenez la mission.</p>
            </div>
            <div className="relative flex gap-3">
              <Link to="/videos" className="px-6 py-3 rounded-full border border-gold text-gold-light font-semibold hover:bg-gold hover:text-white transition-all duration-300">
                Les enseignements
              </Link>
              <Link to="/faire-un-don" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gold text-white font-semibold hover:bg-gold-light transition-all duration-300">
                Faire un don <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
